/* eslint-disable react/display-name */
import React, { useEffect, useState } from "react";
import {
  Paper,
  Grid,
  Button,
  Typography,
  makeStyles
} from "@material-ui/core";
import { withRouter } from "react-router-dom";
import {
  Edit,
  AddCircleRounded,
  CheckCircleRounded,
  Cancel,
} from "@material-ui/icons";
import { withTranslation } from "react-i18next";
import { Table } from "../../Common/Table";
import { connect } from "react-redux";
import { useRoles } from "../../../hooks/useRoles";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import { useAuth } from "../../../context/auth";
import { checkAccess } from "../utils/index";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
    width: "100%"
  },
  fullWidthContainer: {
    maxWidth: "100%"
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh"
  },
  spacer: {
    marginBottom: 20
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50
  }
}));

const ListRoles = ({ i18n, t, history, match, actionShowSpinner, location }) => {
  const classes = useStyles();
  const { userAccess } = useAuth();

  const { params: { type } } = match;

  const [params, setParams] = useState({
    rolesParam: "",
    frequency: "",
    status: "",
  });
  const [successCreatedState, setSuccessCreatedState] = useState(location?.state?.successCreated);
  const [successUpdatedState, setSuccessUpdatedState] = useState(location?.state?.successUpdated);
  const [successDeletedState, setSuccessDeletedState] = useState(location?.state?.successDeleted);
  
  const [canCreateGeneralRule, setCanCreateGeneralRule] = useState(false);
  const [canUpdateGeneralRule, setCanUpdateGeneralRule] = useState(false);

  useEffect(() => {
    if (typeof location?.state?.successCreated === 'boolean' && location?.state?.successCreated) {
      setTimeout(() => {
        setSuccessCreatedState(false)
      }, 6000)
    }

    if (typeof location?.state?.successUpdated === 'boolean' && location?.state?.successUpdated) {
      setTimeout(() => {
        setSuccessUpdatedState(false)
      }, 6000)
    }

    if (typeof location?.state?.successDeleted === 'boolean' && location?.state?.successDeleted) {
      setTimeout(() => {
        setSuccessDeletedState(false)
      }, 6000)
    }
  }, [location])

  const { getRoles, rolesList } = useRoles({
    spinner: actionShowSpinner,
  });

  useEffect(() => {
    if(userAccess){
      const hasAccess = checkAccess(userAccess, 'listGeneralRules');
      const hasAccessToCreateGeneralRule = checkAccess(userAccess, 'createGeneralRule');
      const hasAccessToUpdateGeneralRule = checkAccess(userAccess, 'updateGeneralRule');
      
      setCanCreateGeneralRule(hasAccessToCreateGeneralRule);
      setCanUpdateGeneralRule(hasAccessToUpdateGeneralRule);

      if(!hasAccess){
        history.push('/');
      }

      getRoles(params); 
    }
  }, [userAccess, checkAccess, params]);

  const rolesCustom = (rowData) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      {rowData.name}
    </div>
  );

  const handleChangeParams = (event) => {
    setParams({ ...params, [event.target.name]: event.target.value });
  };

  const columns = [
    {
      title: t("forms.role.labels.roleName").toUpperCase(),
      field: "name",
      render: (rowData) => rolesCustom(rowData),
    },
    {
      title: t("description").toUpperCase(),
      field: "description",
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item className={classes.spacer}>
        <Typography variant="h4">
          {t("roles")}
        </Typography>
      </Grid>
      <Grid container item lg={12}>
        <Paper elevation={1} className={classes.root}>
          <Grid container item spacing={3} className={classes.fullWidthContainer}>
            <Grid item md={12} lg={12} className={classes.fullWidthContainer}>
              <Table
                columns={columns}
                data={rolesList}
                actions={[
                  {
                    icon: () => <Edit />,
                    tooltip: t("edit"),
                    disabled: !canUpdateGeneralRule,
                    onClick: (_event, { id }) => {
                      history.push(`/role/edit/${id}`);
                    },
                  }
                ]}
              />
            </Grid>
            <Grid item lg={12} className={classes.fullWidthContainer}>
              {successDeletedState && (
                <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                  <Grid item>
                    {
                      <CheckCircleRounded className={classes.icon} />
                    }
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("deleteSuccess").toUpperCase()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {successUpdatedState && (
                <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                  <Grid item>
                    {
                      <CheckCircleRounded className={classes.icon} />
                    }
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("editSuccess").toUpperCase()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {successCreatedState && (
                <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                  <Grid item>
                    {
                      <CheckCircleRounded className={classes.icon} />
                    }
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("addSuccess").toUpperCase()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {type && (
                <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                  <Grid item>
                    {
                      type === "deleteSuccess" ? <Cancel className={classes.icon} /> :
                        <CheckCircleRounded className={classes.icon} />
                    }
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t(type).toUpperCase()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid container direction="row" justify="center" alignItems="center">
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            startIcon={<AddCircleRounded style={{ fontSize: 30 }} />}
            onClick={() => history.push("/role/new")}
            disabled={!canCreateGeneralRule}
          >
            {t("addRoleTable")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const withRouterListRoles = withRouter(ListRoles);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const ListRolesConnect = connect(
  null,
  mapDispatchToProps
)(withRouterListRoles);

export default withTranslation()(ListRolesConnect);
